// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect } from 'react'
import { getI18n } from '../../../../utils/intl.js'
import { useIntl } from 'react-intl';

import PropTypes from 'prop-types';
import {
  Grid, makeStyles,
  Collapse,
  Divider,
  Typography,
  Tooltip,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';

import { FormTextField, Switch } from '../../../../components';
import styles from './styles';
import * as CONSTANTS from '../../../../constants';

export const AuthProviderOverview = (props) => {

  const {
    authProviderUuid,
    issuerChangeCallback,
    authHeaderChangeCallback,
    dcrChangeCallback,
    sanitizedFieldsCallback,
    errors=[],
  } = props;

  const AUTH_PROVIDER_ISSUER_MAX_LENGTH = 2048;
  const [issuerName, setIssuerName] = useState(props.issuerName || '');
  const [authHeader, setAuthHeader] = useState(props.authHeader || '');
  const [isDcr, setIsDcr] = useState(false);
  const [sanitizedFields, setSanitizedFields] = useState(props.sanitizedFields || '');

  useEffect(() => {
    if (props.issuerName) {
      setIssuerName(props.issuerName);
      setAuthHeader(props.authHeader);
      setIsDcr(props.clientRegistrationType === CONSTANTS.AUTH_PROVIDER_TYPE_PORTAL_DCR);
      setSanitizedFields(props.sanitizedFields);
    }
  }, [props.issuerName]);

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const handleIssuerNameChange = (value) => {
    setIssuerName(value)
    if (issuerChangeCallback) {
      issuerChangeCallback(value)
    }
  }

  const handleAuthHeaderChange = (value) => {
    setAuthHeader(value)
    if (authHeaderChangeCallback) {
      authHeaderChangeCallback(value)
    }
  }

  const handleIsDCRChange = (state) => {
    setIsDcr(state);
    if (dcrChangeCallback) {
      dcrChangeCallback(state);
    }
  }

  const hanldeSanitizeFieldChange = (value) => {
    setSanitizedFields(value)
    if (sanitizedFieldsCallback) {
      sanitizedFieldsCallback(value)
    }
  }

  return (
    <div className={classes.content}>
      <Grid item xs={12}>
        <Typography className={classes.issuerLabel} variant="h4">
          {intl.getI18nMessage('label.auth.providers.list.issuer')}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormTextField
          id='add-auth-provider-issuer-field'
          maxLength={AUTH_PROVIDER_ISSUER_MAX_LENGTH}
          helperText={intl.getI18nMessage("label.auth.providers.issuer.helper.text")}
          placeholder={intl.getI18nMessage("label.auth.providers.details.issuer.place.holder")}
          handleChange={handleIssuerNameChange}
          value={issuerName}
          error={errors.some((field)=> field.trim().toLowerCase().startsWith('issuer'))}
        />
      </Grid>
      <Grid item xs={12}>
        <Grid container direction={'row'} justifyContent={'space-between'}>
          <Grid item>
            <Typography variant="h4">
              {intl.getI18nMessage("label.auth.providers.details.portal.dcr.title")}
            </Typography>
          </Grid>
          <Grid item className={classes.toggleContainer}>
            <Switch
              disabled={Boolean(authProviderUuid)}
              checked={isDcr}
              id={'portal-dcr-key-enabled'}
              onChange={(checked) => handleIsDCRChange(checked)}
            />
          </Grid>
        </Grid>
        <Divider />
        <Typography className={classes.switchHelpText} variant="body1">
          {intl.getI18nMessage("label.auth.providers.details.portal.dcr.subText")}
        </Typography>
        <Collapse
          in={isDcr}
          unmountOnExit
        >
          <FormTextField
            name={
              <div className={classes.helpIconContainer}>
                <div>
                  {intl.getI18nMessage('label.auth.providers.details.portal.dcr.auth.header')}
                </div>
                <Tooltip title={intl.getI18nMessage('label.auth.providers.details.portal.dcr.auth.header.tooltip')} arrow placement="right">
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </div>
            }
            id={'dcr-auth-header'}
            data-apim-test={'dcr-auth-header'}
            variant="standard"
            value={authHeader}
            handleChange={handleAuthHeaderChange}
            // error={expiryReportingError}
            helperText={intl.getI18nMessage('label.auth.providers.details.portal.dcr.auth.header.helper.text')}
            placeholder={intl.getI18nMessage('label.auth.providers.details.portal.dcr.auth.header.placeholder')}
            optional
          />
          <FormTextField
            name={
              <div className={classes.helpIconContainer}>
                <div>
                  {intl.getI18nMessage("label.auth.providers.details.portal.dcr.fields.sanitize")}
                </div>
                <Tooltip title={intl.getI18nMessage('label.auth.providers.details.portal.dcr.fields.sanitize.tooltip')} arrow placement="right">
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </div>
            }
            id={'dcr-sanitized-fields'}
            data-apim-test={'dcr-sanitized-fields'}
            variant="standard"
            value={sanitizedFields}
            handleChange={hanldeSanitizeFieldChange}
            // error={expiryReportingError}
            helperText={intl.getI18nMessage('label.auth.providers.details.portal.dcr.fields.sanitize.helper.text')}
            placeholder={intl.getI18nMessage('label.auth.providers.details.portal.dcr.fields.sanitize.placeholder')}
            optional
          />
        </Collapse>
      </Grid>
    </div>
  )
};

AuthProviderOverview.propTypes = {
  authProviderUuid: PropTypes.string,
  issuerName: PropTypes.string,
  authHeader: PropTypes.string,
  clientRegistrationType: PropTypes.string,
  sanitizedFields: PropTypes.string,
  issuerChangeCallback: PropTypes.func,
  authHeaderChangeCallback: PropTypes.func,
  dcrChangeCallback: PropTypes.func,
  sanitizedFieldsCallback: PropTypes.func,
  errors: PropTypes.array,
};

export default AuthProviderOverview;
